import { TableFilterState, TableSortingState } from '@ibsys/kit';
import { generatePath } from 'react-router-dom';
import { apiClient } from '@/shared/api';
import { tableFilters2Query, tableSorting2Query } from '@/shared/lib';
import type {
  CommentAuthorListResponse,
  CommentCreateRequest,
  CommentCreateResponse,
  CommentListResponse,
  CommentResponse,
  ConversationAttachmentSignedUrlResponse,
  ConversationChecklist,
  ConversationCreate,
  ConversationInfoResponse,
  ConversationListResponse,
  ConversationResponse,
  ConversationSignedUrlResponse,
  ClientListResponse,
  EmployeeListResponse,
  FileUploadResponse,
  PhraseResponse,
} from './types';
import type { ConversationAttributeListResponse } from '@/entities/attributes/model/api/types';

// Re-export types for convenience
export type {
  CommentCreateRequest,
  CommentCreateResponse,
  CommentListResponse,
  CommentResponse,
  ConversationAttachmentSignedUrlResponse,
  ConversationCreate,
  ConversationInfoResponse,
  ConversationListResponse,
  ConversationResponse,
  ConversationSignedUrlResponse,
};

/**
 * Upload audio file per OpenAPI POST /conversation/audiofile
 * Загрузка аудиофайла в хранилище MinIO для дальнейшей обработки разговора.
 * Требуется разрешение ADMIN_CONVERSATION.
 * Поддерживаются форматы: mp3, wav, m4a, aac, flac. Максимальный размер файла: 100MB.
 */
export const uploadConversationAudio = async (file: File): Promise<FileUploadResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await apiClient.post<FileUploadResponse>('/conversation/audiofile', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return data;
};

/**
 * Query parameters for conversations list per OpenAPI GET /conversation
 * Поддерживается пагинация, фильтрация и сортировка.
 * Фильтрация по датам, сотрудникам, рейтингу, чек-листам и атрибутам.
 * Для фильтрации по атрибутам используйте параметры вида attr_{attribute_code}={value}
 */
export type ConversationsQueryParams = {
  /** Количество записей (1-100, default: 50) */
  limit?: number;
  /** Смещение (minimum: 0, default: 0) */
  offset?: number;
  /** Сортировка по полям с указанием направления (asc/desc) */
  sorting?: TableSortingState;
  /** Фильтры по полям (даты, сотрудники, рейтинг, чек-листы, атрибуты) */
  filters?: TableFilterState;
};

/**
 * Create a new conversation per OpenAPI PUT /conversation
 * Создание нового разговора с участниками, свойствами и метаданными.
 * Требуется разрешение ADMIN_CONVERSATION.
 * Позволяет создавать новых участников или обновлять существующих.
 */
export const createConversation = async (payload: ConversationCreate): Promise<ConversationResponse> => {
  const { data } = await apiClient.put<ConversationResponse>('/conversation', payload);
  return data;
};

/**
 * Fetch a list of conversations per OpenAPI GET /conversation
 * Получение списка разговоров с возможностью пагинации, фильтрации и сортировки.
 * Поддерживается множественная сортировка по полям с указанием направления (asc/desc).
 * Фильтрация по датам, сотрудникам, рейтингу, чек-листам и атрибутам.
 * Для фильтрации по атрибутам используйте параметры вида attr_{attribute_code}={value}
 */
export const fetchConversations = async ({
  limit,
  offset,
  sorting = [],
  filters = [],
}: ConversationsQueryParams): Promise<ConversationListResponse> => {
  const sortParams = tableSorting2Query(sorting);
  const filterParams = tableFilters2Query(filters);

  const { data } = await apiClient.get<ConversationListResponse>('/conversation', {
    params: {
      limit,
      offset,
      sort: sortParams.length > 0 ? sortParams : undefined,
      ...filterParams,
    },
  });
  return data;
};

/**
 * Fetch a conversation by ID per OpenAPI GET /conversation/{id}
 * Получение карточки диалога с атрибутами и участниками.
 * Требуется разрешение VIEW_CONVERSATION.
 * Применяется фильтрация по группам пользователя через SmartPredicateService.
 */
export const fetchConversation = async (
  id: number | string,
  options: { signal: AbortSignal },
): Promise<ConversationInfoResponse> => {
  const { data } = await apiClient.get<ConversationInfoResponse>(`/conversation/${id}`, options);
  return data;
};

/**
 * Fetch conversation transcript per OpenAPI GET /conversation/{id}/transcript
 * Получение полного транскрипта разговора в виде упорядоченного списка фраз
 * с информацией о говорящих и временных метках.
 * Включает все типы событий: PHRASE (фразы), INTERRUPTION (перебивания),
 * PAUSE (паузы), STOP_WORD (стоп-слова).
 * @param id - Conversation ID
 * @param anonymized - Вид текста реплик: true — анонимизированный, false — неанонимизированный
 *   (требует право VIEW_NON_ANONYMIZED_CONVERSATION на бэке)
 * @returns Promise with transcript phrases
 */
export const fetchConversationTranscript = async (
  id: number | string,
  anonymized: boolean,
  options: { signal: AbortSignal },
): Promise<PhraseResponse[]> => {
  const { data } = await apiClient.get<PhraseResponse[]>(
    generatePath('/conversation/:id/transcript', { id: id?.toString() }),
    { params: { anonymized }, signal: options.signal },
  );
  return data;
};

/**
 * Fetch audio file for a conversation per OpenAPI GET /conversation/{id}/audiofile
 * Скачивание оригинального аудиофайла разговора из хранилища MinIO.
 * Поддерживает скачивание частями через заголовок Range для потокового воспроизведения.
 * @param id - Conversation ID
 * @returns Promise with audio blob
 */
export const fetchConversationAudioBlob = async (id: number | string): Promise<Blob> => {
  const { data } = await apiClient.get<Blob>(generatePath('/conversation/:id/audiofile', { id: id?.toString() }), {
    responseType: 'blob',
    silentError: true,
  });
  return data;
};

/**
 * Fetch audio file URL for a conversation
 * Скачивание оригинального аудиофайла разговора и создание URL для воспроизведения.
 * @param id - Conversation ID
 * @returns Promise with audio URL
 */
export const fetchConversationAudioUrl = async (id: number | string): Promise<string> => {
  const { data } = await apiClient.get<Blob>(generatePath('/conversation/:id/audiofile', { id: id?.toString() }), {
    responseType: 'blob',
    silentError: true,
  });
  return URL.createObjectURL(data);
};

/**
 * Fetch conversation checklist assessment per OpenAPI GET /conversation/{conversation_id}/checklist/{checklist_id}
 * Получение результатов оценки качества разговора по указанному чек-листу
 * с детализацией по блокам и критериям.
 * @param conversationId - Conversation ID
 * @param checklistId - Checklist ID
 * @returns Promise with checklist assessment
 */
export const fetchConversationChecklist = async (
  conversationId: number,
  checklistId: number,
): Promise<ConversationChecklist> => {
  const { data } = await apiClient.get<ConversationChecklist>(
    `/conversation/${conversationId}/checklist/${checklistId}`,
  );
  return data;
};

/**
 * Fetch conversations employees per OpenAPI GET /conversation/employee
 * Получение списка сотрудников (role=EMPLOYEE) с возможностью фильтрации
 * по имени/фамилии/отчеству и пагинации.
 * Фильтр поиска работает без учета регистра.
 */
export const fetchConversationsEmployees = async (
  params?: {
    /** Количество записей (1-100, default: 50) */
    limit?: number;
    /** Смещение (minimum: 0, default: 0) */
    offset?: number;
    /** Фильтр по имени/фамилии/отчеству */
    'employee-name'?: string | null;
    /** Точечная выборка по идентификаторам через запятую (не более 1000): пагинация к ней не применяется */
    'employee-id'?: string;
  },
  /** Сигнал отмены: отдельным аргументом, чтобы не попасть в query string вместе с params */
  signal?: AbortSignal,
): Promise<EmployeeListResponse> => {
  const { data } = await apiClient.get<EmployeeListResponse>('/conversation/employee', { params, signal });
  return data;
};

/**
 * Получение списка клиентов (role=CLIENT) по OpenAPI GET /conversation/client.
 * Поддерживает фильтрацию по имени/фамилии/отчеству и пагинацию.
 * Фильтр поиска работает без учёта регистра.
 */
export const fetchConversationsClients = async (
  params?: {
    /** Количество записей (1-1000, default: 50) */
    limit?: number;
    /** Смещение (minimum: 0, default: 0) */
    offset?: number;
    /** Фильтр по имени/фамилии/отчеству */
    'client-name'?: string | null;
    /** Точечная выборка по идентификаторам через запятую (не более 1000): пагинация к ней не применяется */
    'client-id'?: string;
  },
  /** Сигнал отмены: отдельным аргументом, чтобы не попасть в query string вместе с params */
  signal?: AbortSignal,
): Promise<ClientListResponse> => {
  const { data } = await apiClient.get<ClientListResponse>('/conversation/client', { params, signal });
  return data;
};

/**
 * Fetch conversations attributes per OpenAPI GET /conversation/attributes
 * Получение списка атрибутов для формирования таблицы с оцененными диалогами.
 * Требуется разрешение VIEW_CONVERSATION.
 * Поддерживается фильтрация по флагу удаления, группе атрибутов и пагинация.
 */
export const fetchConversationsAttributes = async (params?: {
  /** Фильтр по флагу удаления */
  'is-deleted'?: boolean | null;
  /** Количество записей (1-1000, default: 50) */
  limit?: number;
  /** Смещение (minimum: 0, default: 0) */
  offset?: number;
  /** Фильтр по кодам групп атрибутов (через запятую) */
  'attribute-group-code'?: string | null;
  /** Фильтр по типу атрибута (SYSTEM, COMPUTED_TRANSCRIBATION, COMPUTED_RAW, EXPERT_REPORT_RATING, ANALYTICS) */
  type?: string | string[] | null;
}): Promise<ConversationAttributeListResponse> => {
  const { data } = await apiClient.get<ConversationAttributeListResponse>('/conversation/attributes', { params });
  return data;
};

/**
 * Fetch comments for a conversation per OpenAPI GET /conversation/{id}/comment
 * Получение списка комментариев к диалогу с пагинацией.
 * Сортировка по дате создания (сначала новые).
 * Требуется разрешение VIEW_CONVERSATION.
 */
export const fetchConversationComments = async (
  id: number | string,
  params?: {
    /** Количество записей (1-100, default: 50) */
    limit?: number;
    /** Смещение (minimum: 0, default: 0) */
    offset?: number;
  },
): Promise<CommentListResponse> => {
  const { data } = await apiClient.get<CommentListResponse>(`/conversation/${id}/comment`, {
    params: { limit: params?.limit, offset: params?.offset },
  });
  return data;
};

/**
 * Fetch comment authors per OpenAPI GET /conversation/comment/authors
 * Список пользователей, оставивших хотя бы один комментарий к диалогу — наполняет
 * выпадающий список выбора автора в поиске по комментариям.
 * Поддерживает фильтр по частям имени (без учёта регистра) и пагинацию.
 * Требуется разрешение VIEW_CONVERSATION.
 */
export const fetchConversationCommentAuthors = async (params?: {
  /** Фильтр по частям имени автора (имя/отчество/фамилия) */
  name?: string | null;
  /** Количество записей (1-100, default: 50) */
  limit?: number;
  /** Смещение (minimum: 0, default: 0) */
  offset?: number;
}): Promise<CommentAuthorListResponse> => {
  const { data } = await apiClient.get<CommentAuthorListResponse>('/conversation/comment/authors', { params });
  return data;
};

/**
 * Create a comment for a conversation per OpenAPI POST /conversation/{id}/comment
 * Создание нового комментария к диалогу.
 * Требуется разрешение VIEW_CONVERSATION.
 */
export const createConversationComment = async (
  id: number | string,
  payload: CommentCreateRequest,
): Promise<CommentCreateResponse> => {
  const { data } = await apiClient.post<CommentCreateResponse>(`/conversation/${id}/comment`, payload);
  return data;
};

/**
 * Update a comment per OpenAPI PUT /conversation/comment/{id}
 * Редактирование комментария. Только автор комментария может его редактировать.
 * Требуется разрешение VIEW_CONVERSATION.
 */
export const updateConversationComment = async (
  id: number | string,
  payload: CommentCreateRequest,
): Promise<CommentResponse> => {
  const { data } = await apiClient.put<CommentResponse>(`/conversation/comment/${id}`, payload);
  return data;
};

/**
 * Delete a comment per OpenAPI DELETE /conversation/comment/{id}
 * Удаление комментария. Только автор комментария может его удалить.
 * Требуется разрешение VIEW_CONVERSATION.
 */
export const deleteConversationComment = async (id: number | string): Promise<void> => {
  await apiClient.delete(`/conversation/comment/${id}`);
};

/**
 * Run speech analytics for a conversation per OpenAPI POST /conversation/{id}/speech-analytics
 * Запускает речевую аналитику по звонку. Метод определяет перебивания, паузы, стоп-слова
 * и вычисляет метрики речи для сохранения в атрибуты диалога.
 * Требуется разрешение ADMIN_CONVERSATION.
 */
export const runSpeechAnalytics = async (id: number | string): Promise<void> => {
  await apiClient.post(`/conversation/${id}/speech-analytics`);
};

/**
 * Скачивание транскрипции диалога в формате .docx или .pdf
 * GET /conversation/{id}/transcript/download?format={format}
 * Требуется разрешение VIEW_CONVERSATION.
 * @param id - Conversation ID
 * @param format - Формат файла: 'docx' | 'pdf' | 'xlsx'
 * @returns Promise with file Blob
 */
export const downloadConversationTranscript = async (
  id: number | string,
  format: 'docx' | 'pdf' | 'xlsx',
): Promise<Blob> => {
  const { data } = await apiClient.get<Blob>(
    generatePath('/conversation/:id/transcript/download', { id: id?.toString() }),
    {
      params: { format },
      responseType: 'blob',
    },
  );
  return data;
};

/**
 * Fetch signed URL parameters for a conversation per OpenAPI GET /conversation/{id}/signed-url
 * Получение параметров подписанной ссылки для доступа к ресурсу разговора.
 * @param id - Conversation ID
 * @returns Promise with signed URL response
 */
export const fetchConversationSignedUrl = async (id: number | string): Promise<ConversationSignedUrlResponse> => {
  const { data } = await apiClient.get<ConversationSignedUrlResponse>(
    generatePath('/conversation/:id/signed-url', { id: id?.toString() }),
    {
      silentError: true,
    },
  );
  return data;
};

/**
 * Получение подписанной ссылки на вложение диалога per spec api/conversation/attachment-signed-url.md
 * (GET /conversation/attachment/{attachmentId}/signed-url).
 * Возвращает подписанную ссылку на файл из внутреннего хранилища (MinIO).
 * Применимо только для вложений с isExternalUrl=false.
 */
export const fetchConversationAttachmentSignedUrl = async (
  attachmentId: number | string,
): Promise<ConversationAttachmentSignedUrlResponse> => {
  const { data } = await apiClient.get<ConversationAttachmentSignedUrlResponse>(
    generatePath('/conversation/attachment/:attachmentId/signed-url', {
      attachmentId: attachmentId?.toString(),
    }),
  );
  return data;
};
